// @flow

import * as React from 'react';

/**
 * A typeahead result item from a specific fetcher. The properties may vary based on which fetcher / API was used.
 *
 * This is intended to be an internal typeahead object and not exposed outside of common. Actual selected results are
 * exposed as the 'TypeaheadResult' type.
 */
export type FetcherResult = Object;

/**
 * Typeahead results from a specific fetcher. Note that we are distinguishing between an empty Array and null:
 * - An empty array indicates the fetcher didn't find any search results for the term
 * - Null indicates that the fetcher couldn't perform the fetch due to some constraints or an error.
 *
 * This is intended to be an internal typeahead object and not exposed outside of common. Actual selected results are
 * exposed as the 'TypeaheadResult' type.
 */
export type FetcherResults = Array<FetcherResult> | null;

/**
 * Typeahead results from all fetchers indexed by a fetcher ID. The order of the fetcher IDs matches the order of the
 * fetchers provided.
 *
 * This is intended to be an internal typeahead object and not exposed outside of common. Actual selected results are
 * exposed as the 'TypeaheadResult' type.
 */
export type FetcherResultsMap = {| [string]: FetcherResults |};

/**
 * An interface for a typeahead fetcher. All fetchers should implement this interface.
 */
export interface Fetcher {
  /** A unique ID for this fetcher to group the results */
  +id: string;

  /**
   * Fetch results for 'term' and notify <Typeahead /> by calling onChange() when there are results or an error.
   *
   * Note that this method can be called multiple times before the first fetch completes.  In that case onChange()
   * should not be called until results for the most recently fetched term are available.  We don't want to display
   * stale results.  Similarly, we don't want the previous search results to disappear until new results are ready.
   * The UI should jump as little as possible.
   *
   * An optional context may be passed in addition to the search term to assist the fetchers in processing
   */
  fetch(term: string, context: ?{ [string]: any }, onChange: () => mixed): void;

  /**
   * Get the results for the most recently fetched term
   * We expect the results to be one of:
   * - Non-empty array if there are results in the response
   * - Empty array if there are no results in the response (i.e. no matches)
   * - Null if the query wasn't processed. Could be due to error or some other constraint such as min query length.
   */
  getResults(): FetcherResults;
}

/**
 * An interface used in addition to 'Fetcher' to indicate that this fetcher wants to be made aware of context changes
 * that occur outside of the normal query change event path.
 */
export interface ContextAwareFetcher {
  onContextChange(context: ?{ [string]: any }, onChange: () => mixed): void;
}

/** A filter function to apply to each result */
export type Filter = (result: FetcherResult) => boolean;

type Props = {|
  /** The data fetchers for this typeahead. Results will be displayed according to the order of the fetchers */
  fetchers: Array<Fetcher>,

  /** An optional context object that will be passed to the fetchers along with query changes */
  context?: { [string]: any },

  /**
   * React children that take a render props of typeahead results and a callback to update the search term. The callback
   * also takes an an optional context for additional information required to process the query.
   */
  children: (currentResults: FetcherResultsMap, updateSearch: (term: string) => void) => React.Node,

  /**
   * A callback function whenever the result map of the typeahead changes
   *
   */
  onResultsChange: FetcherResultsMap => void,

  /**
   * An optional array of filters to apply across all results from all fetchers. Each function will be called
   * for each results in the order supplied prior to re-render.
   */
  filters?: Array<Filter>,

  /**
   * Run the typeahead search on initialization with an empty search term. Used when we want to immediately display
   * results that are independent of the terms such as recently viewed. Defaults to false.
   */
  runOnInit?: boolean,
|};

type State = {|
  /** The current result set which has been stitched together from all fetcher results */
  results: FetcherResultsMap,
|};

/**
 * A generic typeahead component that gets data from one or more 'fetchers' and provide it as a render prop to its'
 * children. The results are sent as a collection of result arrays indexed by the id of each fetcher so you can treat
 * them differently. The order of the results matches the order of the fetchers provided. The children components also
 * get a render prop callback function to indicate when the search term has changed.
 */
class Typeahead extends React.Component<Props, State> {
  static defaultProps = {
    context: undefined,
    filters: undefined,
    onResultsChange: () => {},
    runOnInit: false,
  };

  state = {
    results: Object.freeze({}), // freezing makes it exact-typed f
  };

  componentDidMount() {
    if (this.props.runOnInit) {
      this.notifyFetchers('');
    }
  }

  componentDidUpdate(prevProps: Props) {
    // if the context changes then notify all fetchers that implement 'ContextAwareFetcher'
    if (this.props.context !== prevProps.context) {
      this.props.fetchers.forEach(fetcher => {
        // sometimes the fetcher implements this interface
        const maybeContextAwareFetcher = ((fetcher: any): ContextAwareFetcher);

        if (
          maybeContextAwareFetcher.onContextChange &&
          typeof maybeContextAwareFetcher.onContextChange === 'function'
        ) {
          maybeContextAwareFetcher.onContextChange(this.props.context, this.recalculate);
        }
      });
    }
  }

  /**
   * Notify the fetchers that the search term has changed. If this triggers a change to the individual fetcher results
   * then the fetcher will call back to 'recalculate' which is passed as a param.
   */
  notifyFetchers = (term: string) => {
    this.props.fetchers.forEach(fetcher =>
      fetcher.fetch(term, this.props.context, this.recalculate),
    );
  };

  /**
   * Recalculate the results by querying each of the fetchers.
   */
  recalculate = () => {
    const results = {};

    // for each fetcher get the results, apply any filters, and add to 'results' indexed by fetcher id
    this.props.fetchers.forEach(fetcher => {
      const r = fetcher.getResults();

      // if there are results for the fetcher then filter them
      results[fetcher.id] = r
        ? r.filter(result => result && (this.props.filters || []).every(filter => filter(result)))
        : r;
    });

    this.setState(
      {
        results: ((results: any): FetcherResultsMap), // ugh, I give up...
      },
      () => this.props.onResultsChange(this.state.results),
    );
  };

  render() {
    return this.props.children
      ? this.props.children(this.state.results, this.notifyFetchers)
      : null;
  }
}

export default Typeahead;
